import { useTranslation } from 'react-i18next';
import React, {useState, useEffect } from "react";
import { useNavigate, useBlocker, useParams } from "react-router";
import { useAtom, useSetAtom } from "jotai";
import { unreadCoachChatsAtom, toastAtom } from "../../jotai_state";
import Api from "../../services/Api";
import LocalBreadcrumbs from "../../components/Breadcrumbs/LocalBreadcrumbs";
import {
  BookedMeeting,
  FeedbackResultInterface,
  FocusAreaInterface,
  ClientLicenseInterface,
} from "../../interfaces/common";
import profileIcon from "url:shared-assets/icons/profile.svg";
import learningIcon from "url:shared-assets/icons/learning.svg";
import iconClock from "url:shared-assets/icons/clock.svg";
import iconCalendar from "url:shared-assets/icons/calendar.svg";
import iconChat from "url:shared-assets/icons/white/chat.svg"
import iconAlert from "url:shared-assets/icons/red/alert.svg"
import iconInfo from "url:shared-assets/icons/info-filled.svg"
import {Modal, ModalHeader, ModalBody, ModalFooter} from "../../components/Modal";
import DotsPopup from "../../components/Popup/DotsPopup";
import CustomButton from "../../components/CustomButton";
import {dateFormatHourMinute, calculateEndTime, zDate, minutesToTime} from "../../helpers/dateFunctions";
import TabMenu from "../../components/TabMenu/TabMenu";
import {getLanguage} from "../../lang/helpers";
import CancelMeeting from "../../components/CancelMeeting/CancelMeeting";
import BookMeetingFree from "../../components/BookMeeting/BookMeetingFree";
import {openMeeting} from "../../helpers/open_meeting"
import {renderOnTrack} from "../../pages/DevelopmentPlan/helpers";
import {CONFIG} from "../../constants";

enum tabs {
  DEVELOPMENT_PLAN,
  PROFILE,
  MEETINGS,
  INFO,
  NOTES
}

interface VisionStatementInterface {
  id: string;
  title: string;
  description: string;
  deadline: string;
  progress: number;
  onTrack: number;
  goalsTotal: number;
  goalsCompleted: number;
  activitiesTotal: number;
  activitiesCompleted: number;
}

interface CategoryInterface {
  name: string;
  surveyRequestId?: string;
  visionStatements: Array<VisionStatementInterface>;
}

interface StressInterface {
  id: string;
  result: number;
  date: string;
}

interface Big5Interface {
  id: string;
  date: string;
}

interface ClientInterface {
  id: number;
  firstName: string;
  lastName: string;
  picture?: string;
  title: string;
  company: string;
  focusAreas?: Array<FocusAreaInterface>
  meetings?: Array<BookedMeeting>
  leaderShipLevel: string
  inCompanySince: number
  currentRoleSince: number
  coachingExperience: number
  clientLicense: ClientLicenseInterface|null
  categories: Array<CategoryInterface>
  languages: Array<string>
  coachingAreas: Array<string>
  profilesStress: Array<StressInterface>;
  profilesBig5: Array<Big5Interface>;
  dateTo: string|null
}



export default function ClientPage() {

  const { clientId } = useParams();
  const api = Api.getInstance();
  const translation = useTranslation();
  const { t, i18n } = translation;
  const navigate = useNavigate();
  const lang = getLanguage(i18n.language)
  const [client, setClient] = useState<ClientInterface>()
  const [view, setView] = useState<tabs>(tabs.DEVELOPMENT_PLAN);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [showCancelMeeting, setShowCancelMeeting] = useState<boolean>(false)
  const [selectedMeeting, setSelectedMeeting] = useState<BookedMeeting|null>(null)
  const [showChangeMeeting, setShowChangeMeeting] = useState<boolean>(false)
  const [showBookMeeting, setShowBookMeeting] = useState<boolean>(false)
  const [apiError, setApiError] = useState<string>("")
  const [notes, setNotes] = useState<string>("")
  const [notesChanged, setNotesChanged] = useState<boolean>(false)
  const [loaded, setLoaded] = useState<boolean>(false)
  const [newMeeting, setNewMeeting] = useState<BookedMeeting | undefined>();
  const [unreadChats] = useAtom(unreadCoachChatsAtom);
  const setGlobalToast = useSetAtom(toastAtom);

  useEffect(() => {
    getData()
  }, []);

  const ApiError = (error: string) => {
    return (
      <div className="mt-6">
        <h1 className="text-xl">Fatal error!</h1>
        <p className="mt-4">
          {error}
        </p>

      </div>
    );
  }

  let blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      notesChanged && currentLocation.pathname !== nextLocation.pathname
  )

  const getData = () => {
    api.get('coach/client/' + clientId).then((result) => {
      if (result.json.error) {
        console.log(result.json.error)
        setApiError(result.json.error)
      } else {
        const clientJson = result.json.client
        for (const m of clientJson.meetings) {
          m.startDate = new Date(m.start)
          m.firstName = clientJson.firstName;
          m.lastName = clientJson.lastName;
        }
        setClient(clientJson)
        setNotes(result.json.notes)
        setNewMeeting({
          id: "0",
          key: "0",
          length: 30,
          start: "",
          startDate: new Date(),
          clientId: clientId ? clientId : "",
          status: "default",
          firstName: clientJson.firstName,
          lastName: clientJson.lastName
        })
      }
      setLoaded(true)
    })
  }

  const saveNotes = () => {
    const data = {
      notes: notes,
    }
    api.post('coach/' + clientId + '/notes/save', data).then((result) => {
      setNotesChanged(false)
      setGlobalToast({
        text: lang === 'sv' ? 'Ändringar sparade' : 'Changes saved',
      })
    })
  }

  const updateNotes = (txt: string) => {
    setNotes(txt)
    setNotesChanged(true)
  }

  if (!loaded) {
    return (<div className="mt-4">Loading clients...</div>)
  }

  if (apiError.length > 0) {
    return ApiError(apiError);
  }

  const crumbs = [
    { name: t('my-clients'), url: '/clients'},
    { name: client?.firstName + " " + client?.lastName },
  ]

  const getCoachingExperience = (value: number) => {
    if (value === 0) {
      return lang === 'sv' ? "Ingen erfarenhet" : "No experience"
    } else if (value === 1) {
      return lang === 'sv' ? "Lite erfarenhet": "Little experience"
    } else if (value === 2) {
      return lang === 'sv' ? "Måttlig erfarenhet": "Moderate experience"
    } else if (value === 3) {
      return lang === 'sv' ? "Mycket erfarenhet": "Vast experience"
    } else {
      return "Unknown: " + value
    }
  }

  const showLanguages = () => {
    if (!client) return;
    let langs = ""
    let i = 0
    for (const lang of client.languages) {
      if (i > 0) langs += ", "
      langs += t(lang)
      i++
    }
    return langs
  }

  const clickChangeMeeting = (meeting: BookedMeeting) => {
    setSelectedMeeting(meeting)
    setShowChangeMeeting(true)
  }

  const clickCancelMeeting = (meeting: BookedMeeting) => {
    setSelectedMeeting(meeting)
    setShowCancelMeeting(true)
  }

  const closeCancelMeeting = (changed: boolean) => {
    setShowCancelMeeting(false)
    if (changed) {
      getData()
    }
  }

  const closeChangeMeeting = (changed: boolean) => {
    setShowChangeMeeting(false)
    if (changed) {
      getData()
    }
  }

  const renderFocusArea = (feedbackResult: FeedbackResultInterface) => {
    return <div className="p-4">
      {/*<ScoreTable question={feedbackResult}*/}
      {/*            type="focus_area" size="small"/>*/}
      TODO
    </div>
  }

  const goToMeeting = (meetingId: string) => {
    if (!api.apiToken) return
    openMeeting(meetingId, api.apiToken, "coach");
  }

  const goToFocusArea = (focusArea: string) => {
    navigate('/clients/' + clientId + '/focus_area/' + focusArea)
  }

  const renderDevelopmentPlan = () => {
    if (!client) return
    if (client.dateTo) {
      return (
        <div className="mt-6 text-lg">
          {lang === 'sv' ? 'Inte tillgänglig för avslutade klienter': 'Not available for finished clients'}
        </div>
      )
    }
    if (client.categories.length > 0) {
      const longTermGoalUrl = CONFIG.SYSTEM === "production" ? "/clients/vision_statement/" : "/clients/long_term_goal/";
      return (
        <>
          {
            client.categories.map((category, i) => {
              return (
                <div className="mt-6" key={"category_" + i}>
                  <div className="font-medium">{category.name}</div>
                  {
                    category.surveyRequestId &&
                      <div className="mt-2 text-gray-400">
                        <span className="underline hover:text-primary-500 cursor-pointer"
                              onClick={() => navigate('/clients/survey_result/' + category.surveyRequestId + '/summary')}>
                          {lang === "sv" ? "Se insiktsrapport här" : "See the insights report here"}
                        </span>
                      </div>
                  }
                  {
                    category.visionStatements.length === 0 &&
                      <div className="mt-2 text-gray-400">
                        {
                          lang === "sv" ? "Klienten har inte skapat några målbilder än" :
                            "Client hasn't created any Goals yet."
                        }
                      </div>
                  }
                  {
                    category.visionStatements.map(visionStatement => (
                      <div className="bg-indigo-900 hover:bg-indigo-800 mt-2 p-4 rounded-md cursor-pointer"
                           onClick={() => navigate(longTermGoalUrl + visionStatement.id)}
                           key={"vision_" + visionStatement.id}>
                        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-y-2">
                          <div
                            className="flex flex-col md:flex-row md:justify-between gap-x-2 items-start md:items-center">
                            <div className="text-gray-100 z-h3">{visionStatement.title}</div>
                            {renderOnTrack(visionStatement.onTrack, lang)}
                          </div>
                          {
                            visionStatement.deadline &&
                              <div>
                                <span className="z-title3 text-gray-100">
                                  {t("due-date")}:&nbsp;
                                </span>
                                  <span className="text-sm text-gray-400">
                                  {visionStatement.deadline.substring(0, 10)}
                                </span>
                              </div>
                          }
                        </div>

                        <div className="mt-2">
                            <span
                              className="text-gray-100 font-medium">{lang === "sv" ? "Delmål" : "Sub-goals"}:&nbsp;
                            </span>
                          <span className="text-gray-300">
                              {visionStatement.goalsCompleted} {t("of")}&nbsp;
                            {visionStatement.goalsTotal} {t("completed").toLowerCase()}
                            </span>
                        </div>

                        <div className="mt-2">
                            <span
                              className="text-gray-100 font-medium">{lang === "sv" ? "Aktiviteter" : "Activities"}:&nbsp;
                            </span>
                          <span className="text-gray-300">
                              {visionStatement.activitiesCompleted} {t("of")}&nbsp;
                            {visionStatement.activitiesTotal} {t("completed").toLowerCase()}
                            </span>
                        </div>

                        <div className="mt-2 flex items-center">
                          <div className="h-2 rounded-full bg-indigo-700" style={{width: "calc(100% - 40px)"}}>
                            <div className="h-2 rounded-full bg-primary-500"
                                 style={{width: visionStatement.progress + "%"}}></div>
                          </div>
                          <div className="ml-2 text-sm gray-300">{visionStatement.progress}%</div>
                        </div>
                      </div>
                    ))
                  }
                </div>
              )
            })
          }

        </>
      )
    }
    return (
      <>
        <div className="mt-6 text-lg">{t('development-plan')}</div>
        {
          client.focusAreas?.map((focusArea, idx) => (
            <div className="mt-2 bg-neutral-800 rounded-[6px]" key={"fa_" + idx}>
              <div className="p-4 bg-neutral-700 flex items-center rounded-[6px]">
                <img src={learningIcon} alt="learning"/>
                <p className="font-medium ml-2">{focusArea.name}</p>
              </div>
              {
                focusArea.feedbackResult && renderFocusArea(focusArea.feedbackResult)
              }
              <hr className="border-neutral-600"/>
              <div className="p-4">
                <CustomButton className="w-full" buttonType="outlineWhiteBase"
                              onClick={() => goToFocusArea(focusArea.id)}>
                  {t('read')}
                </CustomButton>
              </div>
            </div>
            )
          )
        }
      </>
    )
  }

  const renderMeeting = (meeting: BookedMeeting) => {
    let popItems = [{title: t('to-meeting'), click: () => goToMeeting(meeting.id)}]
    if (meeting.startDate > now) {
      popItems.push(
        {title: t('change-meeting'), click: () => clickChangeMeeting(meeting)},
      )
      popItems.push(
        {title: t('cancel-meeting'), click: () => clickCancelMeeting(meeting)}
      )
    }

    popItems.push(
      {title: t('summary'), click: () => navigate('/clients/' + clientId + '/meeting_summary/' + meeting.id)},
    )

    return (
      <div key={"meeting_" + meeting.id} className="px-4 py-2 bg-neutral-700 flex items-center justify-between">
        <div className="flex items-center">
          <img className="icon" alt="Time" src={iconCalendar}/>
          <span className="ml-2 w-20">
            {zDate(meeting.startDate, lang, false)}
          </span>
          <img className="icon ml-2" alt="Time" src={iconClock}/>
          <span className="ml-2">
              {dateFormatHourMinute(meeting.startDate)}&mdash;{dateFormatHourMinute(calculateEndTime(meeting.startDate, meeting.length))}
          </span>
        </div>

        <DotsPopup items={popItems}/>
      </div>
    )
  }

  const renderProfile = () => {

    if (!client) return;
    return <>

      <div className="mt-6 text-lg">{t('personality-test')}</div>
      {
        client.profilesBig5.map((big, idx) => {
            return (
              <div className="mt-2 z-link" key={"big_" + big.id}
                   onClick={() => navigate('/clients/personality/' + big.id)}>
                {big.date.substring(0, 10)}
              </div>
            )
          }
        )
      }
      {
        client.profilesBig5.length === 0 &&
          <div className="mt-2 text-gray-300">N/A</div>
      }


      <div className="mt-6 text-lg">{t('stress-test')}</div>
      {
        client.profilesStress.map((stress, idx) => {
            return (
              <div className="mt-2 z-link" key={"stress_" + stress.id}
                   onClick={() => navigate('/clients/stress/' + stress.id)}>
                {stress.date.substring(0, 10)}
              </div>
            )
          }
        )
      }
      {
        client.profilesStress.length === 0 &&
          <div className="mt-2 text-gray-300">N/A</div>
      }
    </>
  }

  const renderMeetings = () => {
    if (!client) return;
    const bookedMeetings = client.meetings ? client.meetings.filter(m => (m.startDate > now)) : [];
    const previousMeetings = client.meetings ? client.meetings.filter(m => (m.startDate < now)) : [];
    return <>

      {
        (client.clientLicense?.remainingCredits && client.clientLicense?.remainingCredits >= 30) ? (
          <div className="mt-6">
            <CustomButton className="w-full" buttonType="outlineWhiteBase"
                          onClick={() => setShowBookMeeting(true)}>
              {lang === "sv" ? "Boka nytt möte" : "Book new meeting"}
            </CustomButton>
          </div>
        ) : (
          <div className="mt-6 text-gray-300">
            {lang === "sv" ? "Klienten kan inte boka fler möten." : "The client cannot book moer meetings."}
          </div>
        )

      }

      <div className="mt-6 mb-2 text-lg">{t('booked-meetings')}</div>
      {
        bookedMeetings
          .sort((a, b) => {
              return (b.startDate < a.startDate) ? 1 : -1
            }
          ).map((meeting, idx) => {
            return renderMeeting(meeting)
          }
        )
      }
      {
        bookedMeetings.length === 0 &&
          <div className="mt-6 text-center">
              <img src={iconCalendar} alt="date" className="h-8 w-8 mx-auto"/>
              <p className="mt-4">
                {
                  lang === 'sv' ? 'Du har inga bokade möten med ' : 'You don\'t have any meetings booked with '
                }
                {client.firstName}
              </p>
          </div>
      }

      <div className="mt-6 mb-2 text-lg">{t('previous-meetings')}</div>
      {
        previousMeetings
          .sort((a, b) => {
              return (b.startDate < a.startDate) ? -1 : 1
            }
          ).map((meeting, idx) => {
            return renderMeeting(meeting)
          }
        )
      }
      {
        previousMeetings.length === 0 &&
          <div className="mt-6 text-center">
              <img src={iconCalendar} alt="date" className="h-8 w-8 mx-auto"/>
              <p className="mt-4">
                {
                  lang === 'sv' ? 'Du har inte haft några möten med ' : 'You haven\'t had any meetings booked with '
                }
                {client.firstName}
              </p>
          </div>
      }
    </>
  }

  const renderNotes = () => {
    return <>
      <div className="mt-6 mb-2 text-lg">{t('notes')}</div>
      <div>
        <textarea className="z-input resizeable"  style={{minHeight: 240}} value={notes}
                  onChange={(e) => updateNotes(e.target.value)}></textarea>

      </div>

      <CustomButton className="mt-4" disabled={!notesChanged} onClick={() => saveNotes()}>
        {t('save')}
      </CustomButton>
    </>
  }

  const renderInfo = () => {
    if (!client) return;
    if (client.dateTo) {
      return (
        <div className="mt-6 text-lg">
          {lang === 'sv' ? 'Inte tillgänglig för avslutade klienter': 'Not available for finished clients'}
        </div>
      )
    }
    return (
      <>
        <p className="mt-4 text-lg font-medium">{lang === 'sv' ? 'Coachperiod' : 'Coach period'}</p>
        {
          client.clientLicense ? (
            <>
              <div className="flex mt-2 text-neutral-400 justify-between">
                <p className="font-medium">{lang === 'sv' ? 'Perioden startade' : 'Period started'}</p>
                <p>{client.clientLicense.created.substring(0, 10)}</p>
              </div>
              <div className="flex mt-1 text-neutral-400 justify-between">
                <p className="font-medium">{lang === 'sv' ? 'Perioden slutar' : 'Period ends'}</p>
                <p>{client.clientLicense.end ? client.clientLicense.end.substring(0, 10) : '-'}</p>
              </div>
              <div className="flex mt-1 text-neutral-400 justify-between">
                <p className="font-medium">{lang === 'sv' ? 'Klara möten' : 'Done meetings'}</p>
                <p>{client.clientLicense.hadMeetings} ({minutesToTime(client.clientLicense.hadMeetingTime)})</p>
              </div>
              <div className="flex mt-1 text-neutral-400 justify-between">
                <p className="font-medium">{lang === 'sv' ? 'Bokade möten' : 'Booked meetings'}</p>
                <p>{client.clientLicense.futureMeetings} ({minutesToTime(client.clientLicense.futureMeetingsTime)})</p>
              </div>
              <div className="flex mt-1 text-neutral-400 justify-between">
                <p className="font-medium">{lang === 'sv' ? 'Återstående möten' : 'Remaining meetings'}</p>
                <p>{client.clientLicense.remainingMeetings} ({minutesToTime(client.clientLicense.remainingCredits)})</p>
              </div>
            </>
          ) : (
            <div className="mt-2 font-medium text-neutral-400">No active license</div>
          )
        }

        <hr className="mt-4 border-neutral-600"/>

        <p className="mt-4 text-lg font-medium">{t('professional-details')}</p>
        <div className="flex mt-2 text-neutral-400 justify-between">
          <p className="font-medium">{t('title')}</p>
          <p>{client.title}</p>
        </div>
        <div className="flex mt-1 text-neutral-400 justify-between">
          <p className="font-medium">{t('level')}</p>
          <p>{t(client.leaderShipLevel)}</p>
        </div>
        <div className="flex mt-1 text-neutral-400 justify-between">
          <p className="font-medium">{t('in-company-since')}</p>
          <p>{client.inCompanySince}</p>
        </div>
        <div className="flex mt-1 text-neutral-400 justify-between">
          <p className="font-medium">{t('current-role-since')}</p>
          <p>{client.currentRoleSince}</p>
        </div>

        <hr className="mt-4 border-neutral-600"/>

        <p className="mt-4 text-lg font-medium">{t('experience-of-being-coached')}</p>
        <div className="mt-2 text-neutral-400">
          <p>{getCoachingExperience(client.coachingExperience)}</p>
        </div>

        <hr className="mt-4 border-neutral-600"/>

        <p className="mt-4 text-lg font-medium">{t('languages')}</p>
        <div className="mt-2 text-neutral-400">
          <p>{showLanguages()}</p>
        </div>

        <hr className="mt-4 border-neutral-600"/>

        <p className="mt-4 mb-2 text-lg font-medium">{t('coaching-areas-of-interest')}</p>
        {
          client.coachingAreas.map(area => (
            <div key={"area_" + area} className="mt-1 text-neutral-400">
              {t(area)}
            </div>
          ))
        }
      </>
    )
  }

  const renderTab = () => {
    if (view === tabs.INFO) {
      return renderInfo()
    } else if (view === tabs.PROFILE) {
      return renderProfile()
    } else if (view === tabs.MEETINGS) {
      return renderMeetings()
    } else if (view === tabs.NOTES) {
      return renderNotes()
    } else {
      return renderDevelopmentPlan()
    }
  }

  const now = new Date()

  if (showChangeMeeting && selectedMeeting) {
    if (!clientId || !client || !client.clientLicense) return <></>;
    return (
      <>
        <BookMeetingFree clientId={clientId} remainingCredits={client.clientLicense.remainingCredits}
                         meeting={selectedMeeting} close={closeChangeMeeting} lang={lang}/>
      </>
    )
  }

  if (showBookMeeting) {
    if (!clientId || !client || !client.clientLicense) return <></>;
    return (
      <>
        <BookMeetingFree clientId={clientId} remainingCredits={client.clientLicense.remainingCredits}
                         meeting={newMeeting} close={() => setShowBookMeeting(false)} lang={lang}/>
      </>
    )
  }

  let unreadBadge = null

  if (clientId && unreadChats.indexOf(clientId) > -1) {
    unreadBadge = <img src={iconAlert} alt="alert" className="h-4 w-4"/>
  }

  if (!client) return <></>;

  const blockerProceed = () => {
    if (blocker !== undefined) { // @ts-ignore
      blocker.proceed()
    } else {
      console.error("blocker proceed")
    }
  }

  const blockerReset = () => {
    if (blocker !== undefined) { // @ts-ignore
      blocker.reset()
    } else {
      console.error("blocker reset")
    }
  }

  return (
    <div className="main-content">
      {
        (showCancelMeeting && selectedMeeting) &&
          <CancelMeeting lang={lang} meeting={selectedMeeting}
                         close={closeCancelMeeting}/>
      }

      <LocalBreadcrumbs crumbs={crumbs}/>

      <div className="w-full text-center mb-6">
        <img className="picture-lg mx-auto" alt="pic" src={client.picture ? client.picture : profileIcon}/>

        <div className="mt-4">
          <p className="font-medium text-xl">{client.firstName} {client.lastName}</p>
          <p className="text-neutral-400">{client.title}</p>
          <p className="text-neutral-400">{client.company}</p>
        </div>
      </div>

      <CustomButton className="mb-6" buttonType="outlineWhiteBase"
                    leftIcon={<img src={iconChat} alt="chat" className="h-4 w-4"/>}
                    rightIcon={unreadBadge}
                    onClick={() => navigate('/chats/' + client.id)}>
          Chat
      </CustomButton>

      <TabMenu items={[
        {title: t('development-plan'), callback: () => setView(tabs.DEVELOPMENT_PLAN)},
        {title: t('profile'), callback: () => setView(tabs.PROFILE)},
        {title: t('meetings'), callback: () => setView(tabs.MEETINGS)},
        {title: 'Info', callback: () => setView(tabs.INFO)},
        {title: t('notes'), callback: () => setView(tabs.NOTES)},
      ]}
               active={activeTab}
      />

      {renderTab()}

      {
        blocker.state === "blocked" &&
          <Modal isOpen={true} onClose={() => blockerProceed()}>
              <ModalHeader onClose={() => blockerProceed()}>
                {lang === 'sv' ? 'Anteckningar inte sparade' : 'Notes not saved'}
              </ModalHeader>
              <ModalBody>
                  <>
                      <img src={iconInfo} alt="info" className="text-center h-12 w-12 mt-6 mx-auto"/>
                      <div className="mt-6 text-neutral-400">
                        {
                          lang === 'sv' ?
                            'Du har inte sparat ändringarna i anteckningar. Om du lämnar denna sida nu förlorar du dessa ändringar' :
                            'You have unsaved changes. If you leave this page now, you’ll lose any edits you’ve made.'
                        }
                      </div>
                  </>
              </ModalBody>
              <ModalFooter>
                  <CustomButton buttonType="outlineWhiteBase" onClick={() => blockerProceed()}>
                    {
                      lang === 'sv' ? 'Lämna utan att spara' : 'Leave without saving'
                    }
                  </CustomButton>
                  <CustomButton buttonType="blueBase" onClick={() => blockerReset()}>
                    {
                      lang === 'sv' ? 'Stanna på sidan' : 'Stay on page'
                    }
                  </CustomButton>
              </ModalFooter>
          </Modal>
      }

    </div>

  )
};
